<template>
  <div class="team-container">
    <el-row>
      <el-col :span="isGeneralAdmin || hasFilter ? 12 : 24">
        <organization-breadcrumb :items="[$t($i18nLabelsHelper(currentOrganizationType, 'groups'))]" />
      </el-col>
      <el-col v-if="isGeneralAdmin || hasFilter" :span="12">
        <admin-organization-switcher />
      </el-col>
    </el-row>
    <div class="team-tab-body team-title">
      <div>
        <h2>
          {{ $t($i18nLabelsHelper(currentOrganizationType, "groups")) }}
        </h2>
        <p class="desc">
          {{ $t($i18nLabelsHelper(currentOrganizationType, "groups_desc")) }}
        </p>
      </div>
      <div class="create-new-team" v-if="!isEmptyData && !isUserMigratedToNova">
        <router-link
          :to="'/organization/' + organizationId + '/teams/new'"
          v-if="isOrganizationAdmin || isOrganizationAssociateAdmin"
          tag="button"
        >
          <i class="el-icon-circle-plus" />
          <span style="padding-left: 11px">
            {{ $t($i18nLabelsHelper(currentOrganizationType, "create_new_group")) }}
          </span>
        </router-link>
      </div>
    </div>
    <base-table-loading
      v-if="loading"
      :data="skeletonData"
    />
    <template v-else>
      <div class="team-tab-body" v-if="isEmptyData">
        <div class="no-team">
          <team-icon />
          <h2 style="font-weight: 600; font-size: 32px; text-align: center">
            {{ $t($i18nLabelsHelper(currentOrganizationType, "no_groups_yet")) }}
          </h2>
          <p style="font-size: 16px">{{ $t($i18nLabelsHelper(currentOrganizationType, "no_groups_desc")) }}</p>
          <div class="create-new-team">
            <router-link
              :to="'/organization/' + organizationId + '/teams/new'"
              v-if="isOrganizationAdmin || isOrganizationAssociateAdmin"
              tag="button"
            >
              {{ $t($i18nLabelsHelper(currentOrganizationType, "create_new_group")) }}
            </router-link>
          </div>
        </div>
      </div>
      <elsa-base-table-layout
        v-else
        :showTitle="false"
        :pager-counter="pageCounter"
        :pageSize="numberElementsPerPage"
        :total="teamsCount"
        :currentChange="getTeamPage"
        :current-page.sync="currentPage"
        :queryString.sync="queryString"
        :query-placeholder="$t('common.search')"
        :search="searchTeam"
        :title="$t($i18nLabelsHelper(currentOrganizationType, 'students_page.title'))"
        v-loading="loadingTable"
      >
        <div class="table-wrapper">
          <div class="remove-action">
            <tertiary-button
              v-if="showClassOptions"
              @click.native="openRemoveMultiTeam"
              class="close-button"
              :title="$t($i18nLabelsHelper(currentOrganizationType, 'modals.delete.title'))"
            >
              <font-awesome-icon
                :icon="['fas', 'trash-alt']"
                style="color: #eb5757; font-size: 18px; line-height: 18px"
              />
            </tertiary-button>
          </div>
          <el-table
            style="width: 100%"
            :data="teams"
            @select="handleSelectRow"
            @select-all="handleSelectRow"
            :empty-text="$t('common.no_values')"
          >
            <el-table-column v-if="isOrganizationAdmin || isOrganizationAssociateAdmin" type="selection" />
            <el-table-column
              sortable
              sort-by="name"
              prop="name"
              :label="$t($i18nLabelsHelper(currentOrganizationType, 'group_name'))"
            >
              <template slot-scope="scope">
                <router-link :to="'/organization/' + organizationId + '/classes/' + scope.row.id">
                  <span style="font-size: 16px">{{ scope.row.name }}</span>
                </router-link>
              </template>
            </el-table-column>
            <el-table-column
              sortable
              :sort-method="sortTeacher"
              :label="$t($i18nLabelsHelper(currentOrganizationType, 'teacher'))"
              min-width="150"
            >
              <template slot-scope="scope">
                <div class="wrap-avatar" v-if="scope.row.owner">
                  <initial-avatar
                    v-bind="$attrs"
                    :avatarUrl="scope.row.owner.photo_url"
                    size="large"
                    :userObject="scope.row.owner"
                  />
                  <div class="block">
                    <label style="display: block">
                      {{ scope.row.owner.first_name }}
                      {{ scope.row.owner.last_name }}
                    </label>
                    <label style="color: #6d7a8c">{{ scope.row.owner.email }}</label>
                  </div>
                </div>
                <div class="wrap-avatar" v-else-if="scope.row.pending_teacher">
                  <initial-avatar v-bind="$attrs" size="large" :userObject="scope.row.pending_teacher" />
                  <div class="block">
                    <label class="status__pending">
                      <span class="status__input">
                        <span class="status__inner"></span>
                      </span>
                      <span class="status__label">{{ $t("classes.status.pending") }}</span>
                    </label>
                    <label style="color: #6d7a8c">{{ scope.row.pending_teacher }}</label>
                  </div>
                </div>
                <div v-else class="invite-owner" @click="openInviteTeacher(scope.row)">
                  <i class="el-icon-circle-plus-outline" />
                  {{ $t("organizations_team.invite") }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              sortable
              prop="users_count"
              :label="$t('organizations_team.accepted_learners')"
              align="center"
            />
            <el-table-column
              sortable
              :sort-method="sortTotalLearners"
              :label="$t('organizations_team.total_learners')"
              align="center"
            >
              <template slot-scope="scope">
                <label>{{ scope.row.users_count + scope.row.pending_invitations }}</label>
              </template>
            </el-table-column>
            <el-table-column
              dataType="date"
              :sort-method="sortStartDate"
              sortable
              :label="$t('organizations_team.start_date')"
            >
              <template slot-scope="scope">
                <span>{{ formatDate(scope.row.start_date) }}</span>
              </template>
            </el-table-column>
            <el-table-column
              dataType="date"
              :sort-method="sortEndDate"
              sortable
              :label="$t('organizations_team.end_date')"
            >
              <template slot-scope="scope">
                <span>{{ formatDate(scope.row.end_date) }}</span>
              </template>
            </el-table-column>
            <el-table-column
              sortable
              prop="status"
              :label="$t('organizations_team.status')"
              :filters="[
                { text: $t(`classes.status.active`), value: 'active' },
                { text: $t(`classes.status.finished`), value: 'finished' },
                { text: $t(`classes.status.not_started`), value: 'not_started' },
                { text: $t(`classes.status.pending`), value: 'pending' },
                { text: $t(`classes.status.draft`), value: 'draft' },
              ]"
              :filter-method="filterHandler"
              filter-placement="bottom-end"
            >
              >
              <template slot-scope="scope">
                <label :class="'status__' + scope.row.status">
                  <span class="status__input">
                    <span class="status__inner"></span>
                  </span>
                  <span class="status__label">{{ $t(`classes.status.${scope.row.status}`) }}</span>
                </label>
              </template>
            </el-table-column>
            <el-table-column v-if="!isUserMigratedToNova && (isOrganizationAdmin || isOrganizationAssociateAdmin)">
              <template slot-scope="scope">
                <div class="flex">
                  <span
                    class="edit-team"
                    @click="handleEditTeam(scope.row.id)"
                    :title="$t($i18nLabelsHelper(currentOrganizationType, 'edit_group'))"
                  >
                    <i class="elsa-icon-edit" />
                  </span>
                  <tertiary-button
                    class="close-button"
                    :title="$t($i18nLabelsHelper(currentOrganizationType, 'remove_group'))"
                    @click.native="openRemoveTeam(scope.row)"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'trash-alt']"
                      style="color: #eb5757; font-size: 14px; line-height: 14px"
                    />
                  </tertiary-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </elsa-base-table-layout>
    </template>
    <invite-teacher
      :dialogVisible="showInviteTeacherToTeamModal"
      v-on:closed="showInviteTeacherToTeamModal = false"
      :organizationType="currentOrganizationType"
      @newTeacher="sendInviteTeacher"
    />
    <delete-classes
      :dialogVisible="showDeleteClassesModal"
      v-on:closed="showDeleteClassesModal = false"
      :numberOfSelectedClasses="numberOfSelectedRows"
      :currentOrganizationType="currentOrganizationType"
      @doDeleteClasses="doRemoveTeams"
      :type_remove="type_remove"
    />
  </div>
</template>

<script>
import DeleteClasses from "@/components/modals/classes/DeleteClasses";
import teamIcon from "@/assets/images/inline.team-icon-big.svg";
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import AdminOrganizationSwitcher from "@/components/dashboard/AdminOrganizationSwitcher";
import InitialAvatar from "@/components/common/InitialAvatar";
import InviteTeacher from "@/components/modals/classes/InviteTeacher";
import { format } from "date-fns";
import { mapGetters } from "vuex";
import { TEACHER } from "../constants/userRoles";
import { successMessage, warningMessage } from "@/utils/messages";
import ElsaBaseTableLayout from "@/components/common/ElsaBaseTableLayout";
import { always, pathOr, pluck, propOr, compose, isEmpty, not, length } from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE, PAGE_COUNTER } from "../constants/generic";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import BaseTableLoading from "@/components/common/BaseTableLoading.vue";

export default {
  components: {
    teamIcon,
    OrganizationBreadcrumb,
    InitialAvatar,
    InviteTeacher,
    ElsaBaseTableLayout,
    AdminOrganizationSwitcher,
    DeleteClasses,
    TertiaryButton,
    BaseTableLoading,
  },
  props: {
    dateFormat: {
      type: String,
      default: "MM/dd/yyyy",
    },
  },
  data() {
    return {
      currentPage: 1,
      queryString: null,
      loading: true,
      loadingTable: false,
      showInviteTeacherToTeamModal: false,
      showDeleteClassesModal: false,
      currentClassInvite: null,
      selectedClasses: [],
      type_remove: "single",
      removeClassId: null,
      paginateTeams: {
        results: [],
        count: 0,
      },
      isEmptyData: false,
      skeletonData: [
        {
          name: "Class Name",
          label: "Class Name",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Teacher",
          label: "Teacher",
          layout: [
            {
              width: "30%",
              justifyContent: "center",
              alignItems: "center",
              items: [
                {
                  width: "40px",
                  height: "40px",
                  type: "circle",
                },
              ],
            },
            {
              width: "70%",
              items: [
                {
                  type: "rectangle",
                  width: "60%",
                  height: "12px",
                },
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Accepted Leaners",
          label: "Accepted Leaners",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Total Learners",
          label: "Total Learners",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Start Date",
          label: "Start Date",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "End Date",
          label: "End Date",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
        {
          name: "Status",
          label: "Status",
          layout: [
            {
              width: "100%",
              items: [
                {
                  width: "100%",
                  height: "12px",
                  type: "rectangle",
                },
              ],
            },
          ],
        },
      ],
    };
  },
  computed: {
    showClassOptions: compose(not, isEmpty, propOr([], "selectedClasses")),
    numberOfSelectedRows: compose(length, propOr([], "selectedClasses")),
    organizationId() {
      return this.$store.state.organization.currentOrganizationId;
    },
    organizationData() {
      return this.$store.state.organization.currentOrganization;
    },
    teamsCount() {
      return pathOr(0, ["paginateTeams", "count"])(this);
    },
    teams() {
      return pathOr([], ["paginateTeams", "results"])(this);
    },
    pageCounter: always(PAGE_COUNTER),
    numberElementsPerPage: always(NUMBER_ELEMENTS_PER_PAGE),
    ...mapGetters("organization", [
      "isOrganizationAdmin",
      "isOrganizationAssociateAdmin",
      "currentOrganizationType",
      "isGeneralAdmin",
      "hasFilter",
      "isUserMigratedToNova",
    ]),
  },
  methods: {
    searchTeam(search) {
      this.getTeam({ search });
    },
    getTeamPage(page) {
      const search = this.queryString;
      return this.getTeam({ page, search });
    },
    async getTeam({ page = 1, search = null, status = "" }) {
      try {
        this.loadingTable = true;
        const organizationId = this.organizationId;
        let params = { page, search, status };
        const { data } = await this.$axios.get(`/v1/groups/${organizationId}/`, { params });
        this.paginateTeams = data;
        this.selectedClasses = [];
      } finally {
        this.loadingTable = false;
      }
    },
    formatDate(date) {
      return date ? format(new Date(date), this.dateFormat) : "--";
    },
    getColorByStatus(status) {
      let color;
      switch (status) {
        case "active":
          color = "#03BD23";
          break;
        case "draft":
          color = "#FF9500";
          break;
        case "finished":
          color = "#03BD23";
          break;
        case "not_started":
          color = "#8D8D8D";
          break;
      }
      return color;
    },
    handleEditTeam(classId) {
      return this.$router.push({
        name: "class-edit",
        params: {
          organizationId: this.organizationId,
          classId: classId,
        },
      });
    },
    handleSelectRow(rowDat) {
      this.selectedClasses = rowDat;
    },
    openRemoveMultiTeam() {
      this.showDeleteClassesModal = true;
      this.type_remove = "multi";
    },
    openRemoveTeam(clazz) {
      this.showDeleteClassesModal = true;
      this.type_remove = "single";
      this.removeClassId = clazz.id;
    },
    async doRemoveTeams() {
      let groupIds = [];
      if (this.type_remove == "multi") {
        groupIds = compose(pluck("id"), propOr([], "selectedClasses"))(this);
      } else {
        groupIds = [this.removeClassId];
      }
      const organizationId = this.organizationId;
      const params = { ids: groupIds };
      try {
        await this.$axios.put(`/v1/organizations/${organizationId}/update-groups-draft`, params);
        const message = this.$i18nLabelsHelper(this.currentOrganizationType, "delete_group_success");
        successMessage(this.$t(message))(this);
        return this.getTeam({});
      } catch (error) {
        const message = this.$i18nLabelsHelper(this.currentOrganizationType, "delete_group_warning");
        return warningMessage(this.$t(message))(this);
      }
    },
    openInviteTeacher(clazz) {
      this.showInviteTeacherToTeamModal = true;
      this.currentClassInvite = clazz;
    },
    async sendInviteTeacher({ email, first_name, last_name }) {
      const classObject = this.currentClassInvite;
      const inviteBody = {
        emails: [email],
        first_names: [first_name],
        last_names: [last_name],
        role: TEACHER,
        organization: classObject.organization_id,
        group: classObject.id,
        auto_join: 1,
      };
      try {
        await this.$axios.post("/v2/accounts/invite/", inviteBody);
        const message = this.$t("organizations_team.invited_leader_to_team");
        successMessage(this.$t(message))(this);
        return this.getTeam({});
      } catch (error) {
        const message = this.$t("organizations_team.could_not_invited_leader_to_team");
        return warningMessage(this.$t(message))(this);
      }
    },
    sortTeacher(teamA, teamB) {
      if (teamA.owner && teamB.owner) {
        const nameAConstructable = teamA.owner.first_name || teamA.owner.last_name;
        const nameBConstructable = teamB.owner.first_name || teamB.owner.last_name;
        // Neu ca 2 deu co first name hoac last name
        if (nameAConstructable && nameBConstructable) {
          const nameA = teamA["owner"]["first_name"] + " " + teamA["owner"]["last_name"];
          const nameB = teamB["owner"]["first_name"] + " " + teamB["owner"]["last_name"];
          return nameA.localeCompare(nameB);
        } else if (nameAConstructable) {
          return 1;
        } else if (nameBConstructable) {
          return -1;
        } else {
          return teamA.owner.email.localeCompare(teamB.owner.email);
        }
      } else if (teamA.pending_teacher && teamB.pending_teacher) {
        return teamA.pending_teacher.localeCompare(teamB.pending_teacher);
      } else if (teamA.owner) {
        return 1;
      } else if (teamB.owner) {
        return -1;
      } else if (teamA.pending_teacher) {
        return 1;
      } else if (teamB.pending_teacher) {
        return -1;
      } else {
        return 0;
      }
    },
    sortStartDate(teamA, teamB) {
      if (teamA.start_date && teamB.start_date) {
        return teamA.start_date.localeCompare(teamB.start_date);
      } else if (teamA.start_date) {
        return 1;
      } else if (teamB.start_date) {
        return -1;
      } else {
        return 0;
      }
    },
    sortEndDate(teamA, teamB) {
      if (teamA.end_date && teamB.end_date) {
        return teamA.end_date.localeCompare(teamB.end_date);
      } else if (teamA.end_date) {
        return 1;
      } else if (teamB.end_date) {
        return -1;
      } else {
        return 0;
      }
    },
    sortTotalLearners(teamA, teamB) {
      return teamA.users_count + teamA.pending_invitations - (teamB.users_count + teamB.pending_invitations);
    },
    filterHandler(value, row, column) {
      const property = column["property"];
      return row[property] === value;
    },
  },
  async mounted() {
    try {
      this.loading = true;
      const params = { status: "" };
      await this.getTeam(params);
      if (this.teamsCount == 0) {
        this.isEmptyData = true;
      }
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../scss/_colors.scss";

.team-container {
  width: 100%;
  font-family: "SF Pro Display", sans-serif;
  .team-tab-body {
    padding: 40px;
    text-align: center;
    padding-bottom: 0px;
  }
  .create-new-team {
    button {
      background: #007eff;
      border-radius: 8px;
      border: none;
      color: #fff;
      padding: 16px;
      font-size: 16px;
      max-width: 190px;
      cursor: pointer;
    }
  }
  .el-table .el-table__row .wrap-avatar small {
    font-size: 16px;
  }
  .status__label {
    font-size: 16px;
    text-transform: capitalize;
    padding-left: 10px;
  }
  .status__input {
    vertical-align: middle;
    .status__inner {
      border: 1px solid #dcdfe6;
      border-radius: 100%;
      width: 14px;
      height: 14px;
      background-color: #fff;
      cursor: pointer;
      -webkit-box-sizing: border-box;
      box-sizing: border-box;
      position: relative;
      display: inline-block;
    }
  }
  .status__active .status__inner {
    border-color: #03bd23;
    background-color: #03bd23;
  }
  .status__finished .status__inner {
    border-color: #e6a23c;
    background-color: #e6a23c;
  }
  .status__not_started .status__inner {
    border-color: #909399;
    background-color: #909399;
  }
  .status__pending {
    display: block;
  }
  .status__pending .status__inner {
    border-color: #d9ecff;
    background-color: #d9ecff;
  }
  .wrap-avatar {
    .block {
      word-break: break-word;
    }
  }
  .close-button {
    width: 40px !important;
    height: 40px !important;
    min-width: 40px;
  }
  .edit-team {
    padding-top: 12px;
    padding-right: 20px;
  }
  .team-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;

    h2 {
      font-weight: 600;
      font-size: 32px;
    }

    p.desc {
      font-size: 16px;
      text-align: left;
      margin: 8px 0 32px 0;
    }
  }
}

::v-deep .elsa-table-layout {
  padding-top: 0px;
}

::v-deep .el-table__column-filter-trigger i {
  font-size: 16px;
}
::v-deep .el-table .el-table-column--selection .cell {
  padding-left: 8px;
}
::v-deep .close-button.el-button.is-plain:hover {
  border-color: rgb(235, 87, 87);
}
</style>
